import React, { useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Box, IconButton as MuiIconButton, styled, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import type { DateView } from '@mui/x-date-pickers/models';
import { ReactComponent as CalendarMonthIcon } from '../../../assets/icons/calendar-date-range.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Typography } from '../../atoms';
import { InfoTooltip } from '../../molecules/InfoTooltip';
import ErrorIcon from '@mui/icons-material/Error';

export type DatePickerFTDSSelectionType = 'date' | 'month' | 'year';

export interface DatePickerFTDSProps {
    selectionType: DatePickerFTDSSelectionType;
    value?: Dayjs | null;
    // eslint-disable-next-line no-unused-vars
    onChange: (value: Dayjs | null) => void;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    /** Earliest date visible/navigable in the picker. */
    startDate?: Dayjs;
    /** Latest date visible/navigable in the picker. */
    endDate?: Dayjs;
    /** Values before this date are rendered but shown as disabled and cannot be selected. */
    enabledFrom?: Dayjs;
    /** Date used for unset parts when nothing is selected; unlike the default, it is not clamped to `endDate`. */
    referenceDate?: Dayjs;
    format?: string;
    /** When true, renders the field with an error-coloured border. */
    error?: boolean;
    /** Error text shown under the field; falls back to the picker's own min/max message. */
    helperText?: string;
    /** Overrides the default message when the value is before `startDate`. */
    minDateErrorMsg?: string;
    /** Overrides the default message when the value is after `endDate`. */
    maxDateErrorMsg?: string;
    onValidationError?: Function;
    /** Return false to block the calendar from opening. */
    canOpen?: () => boolean;
}

const DateIconAdornment = styled('span')<{ hasError?: boolean }>(({ theme, hasError }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: hasError ? theme.palette.semantic.bg.error : theme.palette.semantic.bg.brandLight2,
    '&:hover': {
        backgroundColor: theme.palette.semantic.bg.brandLight2
    },
    color: theme.palette.semantic.icon.brand,
    '& svg:not(.error-svg)': { width: 18, height: 18 },
    '& .error-svg': {
        display: 'flex'
    }
}));

const StyledDatePicker = styled(DatePicker<Dayjs>)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        paddingRight: 0,
        overflow: 'hidden',
        '& fieldset': {
            borderColor: theme.palette.semantic.bd.default
        },
        '&:hover fieldset': {
            borderColor: theme.palette.semantic.bd.strong
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.semantic.int.selected,
            borderWidth: '2px'
        },
        '&.Mui-error fieldset': {
            borderColor: theme.palette.error.main
        }
    },
    '& .MuiOutlinedInput-input': {
        fontWeight: 400
    },
    '& .MuiInputAdornment-root': {
        margin: 0,
        marginLeft: '8px',
        height: '100%',
        maxHeight: 'none'
    },
    '& .MuiIconButton-edgeEnd': {
        margin: 0,
        marginRight: 0,
        padding: 0,
        backgroundColor: theme.palette.semantic.bg.brandLight2,
        '&:hover': {
            backgroundColor: theme.palette.semantic.bg.brandLight2
        }
    }
}));

const popperSx = (theme: Theme) => ({
    '& .MuiPaper-root': {
        borderRadius: '12px',
        border: `1px solid ${theme.palette.semantic.bd.subtle}`,
        boxShadow: '0px 8px 24px rgba(0,0,0,0.08)',
        padding: '8px',
        overflow: 'hidden'
    },
    '& .MuiDateCalendar-root': {
        height: 'auto',
        maxHeight: 'none'
    },
    // Day cells (date mode)
    '& .MuiDayCalendar-root .MuiPickersDay-root.Mui-selected': {
        backgroundColor: theme.palette.primary[500],
        color: theme.palette.common.white,
        '&:hover, &:focus': { backgroundColor: theme.palette.primary[500] }
    },
    // Month cells — outlined rectangles matching mockup
    '& .MuiMonthCalendar-root': {
        gap: '8px',
        padding: '8px',
        width: '320px'
    },
    '& .MuiMonthCalendar-root .MuiPickersMonth-root': {
        flexBasis: 'calc(33.333% - 8px)'
    },
    '& .MuiMonthCalendar-root .MuiPickersMonth-monthButton': {
        width: '100%',
        height: '40px',
        margin: 0,
        borderRadius: 0,
        border: `1px solid ${theme.palette.semantic.bd.subtle}`,
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.semantic.text.default,
        backgroundColor: 'transparent',
        '&:hover, &:focus': {
            backgroundColor: theme.palette.semantic.bg.brandLight2,
            borderColor: theme.palette.semantic.bd.subtle
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary[500],
            color: theme.palette.common.white,
            borderColor: theme.palette.primary[500],
            '&:hover, &:focus': {
                backgroundColor: theme.palette.primary[500]
            }
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.semantic.bg.subtle,
            color: theme.palette.semantic.text.muted,
            borderColor: theme.palette.semantic.bd.subtle,
            cursor: 'not-allowed',
            pointerEvents: 'none',
            opacity: 0.6
        }
    },
    // Year cells — same treatment as months
    '& .MuiYearCalendar-root': {
        gap: '8px',
        padding: '8px',
        width: '320px'
    },
    '& .MuiYearCalendar-root .MuiPickersYear-root': {
        flexBasis: 'calc(33.333% - 8px)'
    },
    '& .MuiYearCalendar-root .MuiPickersYear-yearButton': {
        width: '100%',
        height: '40px',
        margin: 0,
        borderRadius: 0,
        border: `1px solid ${theme.palette.semantic.bd.subtle}`,
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.semantic.text.default,
        backgroundColor: 'transparent',
        '&:hover, &:focus': {
            backgroundColor: theme.palette.semantic.bg.brandLight2,
            borderColor: theme.palette.semantic.bd.subtle
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary[500],
            color: theme.palette.common.white,
            borderColor: theme.palette.primary[500],
            '&:hover, &:focus': {
                backgroundColor: theme.palette.primary[500]
            }
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.semantic.bg.subtle,
            color: theme.palette.semantic.text.muted,
            borderColor: theme.palette.semantic.bd.subtle,
            cursor: 'not-allowed',
            pointerEvents: 'none',
            opacity: 0.6
        }
    }
});

interface HeaderProps extends PickersCalendarHeaderProps<Dayjs> {
    selectionType: DatePickerFTDSSelectionType;
    selectedValue?: Dayjs | null;
    enabledFrom?: Dayjs;
    // eslint-disable-next-line no-unused-vars
    onYearStep?: (next: Dayjs) => void;
}

const HeaderChevron = styled(MuiIconButton)(({ theme }) => ({
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: theme.palette.semantic.bg.subtle,
    color: theme.palette.semantic.icon.default,
    '&:hover': { backgroundColor: theme.palette.semantic.int.hover },
    '&.Mui-disabled': {
        opacity: 0.4
    }
}));

const HeaderToggle = styled('button')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.semantic.text.default,
    '&:hover': { backgroundColor: theme.palette.semantic.int.hover },
    '& svg': { width: 18, height: 18 }
}));

const DatePickerFTDSHeader = (props: HeaderProps) => {
    const {
        currentMonth,
        onMonthChange,
        view,
        views,
        onViewChange,
        minDate,
        maxDate,
        selectionType,
        selectedValue,
        enabledFrom,
        onYearStep
    } = props;

    const current = currentMonth;

    const goPrev = () => onMonthChange(current.subtract(1, 'month'), 'right');
    const goNext = () => onMonthChange(current.add(1, 'month'), 'left');

    // Month/year modes: arrows move the selected value by one year, clamped to the selectable range.
    const stepUnit = selectionType === 'year' ? 'year' : 'month';
    const lowerBound = enabledFrom && (!minDate || enabledFrom.isAfter(minDate)) ? enabledFrom : minDate;
    const getYearStep = (delta: number): Dayjs | null => {
        const target = (selectedValue ?? current).add(delta, 'year');
        if (lowerBound && target.isBefore(lowerBound, 'year')) return null;
        if (maxDate && target.isAfter(maxDate, 'year')) return null;
        if (lowerBound && target.isBefore(lowerBound, stepUnit)) return lowerBound.startOf(stepUnit);
        if (maxDate && target.isAfter(maxDate, stepUnit)) return maxDate.startOf(stepUnit);
        return target;
    };
    const prevYear = getYearStep(-1);
    const nextYear = getYearStep(1);

    const canOpenYear = views.includes('year' as DateView);
    const canOpenMonth = views.includes('month' as DateView);
    const toggleView = (target: DateView) => {
        if (view === target) return;
        if (onViewChange) onViewChange(target);
    };

    const prevDisabled = !!(minDate && current.isSame(minDate, 'month'));
    const nextDisabled = !!(maxDate && current.isSame(maxDate, 'month'));

    if (selectionType === 'date') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px'
                }}
            >
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    <HeaderChevron size="small" onClick={goPrev} disabled={prevDisabled} aria-label="Previous month">
                        <KeyboardArrowLeftIcon fontSize="small" />
                    </HeaderChevron>
                    <HeaderChevron size="small" onClick={goNext} disabled={nextDisabled} aria-label="Next month">
                        <KeyboardArrowRightIcon fontSize="small" />
                    </HeaderChevron>
                </Box>
                <Box sx={{ display: 'flex', gap: '12px' }}>
                    {canOpenYear && (
                        <HeaderToggle type="button" onClick={() => toggleView('year' as DateView)}>
                            {current.format('YYYY')}
                            <KeyboardArrowDownIcon />
                        </HeaderToggle>
                    )}
                    {canOpenMonth && (
                        <HeaderToggle type="button" onClick={() => toggleView('month' as DateView)}>
                            {current.format('MMM')}
                            <KeyboardArrowDownIcon />
                        </HeaderToggle>
                    )}
                </Box>
            </Box>
        );
    }

    // month or year mode: chevrons flank a centered year toggle
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px'
            }}
        >
            <HeaderChevron
                size="small"
                onClick={() => prevYear && onYearStep?.(prevYear)}
                disabled={!prevYear}
                aria-label="Previous"
            >
                <KeyboardArrowLeftIcon fontSize="small" />
            </HeaderChevron>
            {canOpenYear && selectionType === 'month' && view === 'month' ? (
                <HeaderToggle type="button" onClick={() => toggleView('year' as DateView)}>
                    {current.format('YYYY')}
                    <KeyboardArrowDownIcon />
                </HeaderToggle>
            ) : (
                <Box sx={{ fontSize: '16px', fontWeight: 600 }}>{current.format('YYYY')}</Box>
            )}
            <HeaderChevron
                size="small"
                onClick={() => nextYear && onYearStep?.(nextYear)}
                disabled={!nextYear}
                aria-label="Next"
            >
                <KeyboardArrowRightIcon fontSize="small" />
            </HeaderChevron>
        </Box>
    );
};

const getModeConfig = (selectionType: DatePickerFTDSSelectionType) => {
    switch (selectionType) {
        case 'year':
            return { views: ['year'] as DateView[], openTo: 'year' as DateView, defaultFormat: 'YYYY' };
        case 'month':
            return {
                views: ['year', 'month'] as DateView[],
                openTo: 'year' as DateView,
                defaultFormat: 'MM/YYYY'
            };
        case 'date':
        default:
            return {
                views: ['year', 'month', 'day'] as DateView[],
                openTo: 'year' as DateView,
                defaultFormat: 'MM/DD/YYYY'
            };
    }
};

export const DatePickerFTDS: React.FC<DatePickerFTDSProps> = ({
    selectionType,
    value,
    onChange,
    label,
    placeholder,
    disabled,
    startDate,
    endDate,
    enabledFrom,
    referenceDate,
    format,
    helperText,
    minDateErrorMsg,
    maxDateErrorMsg,
    error,
    onValidationError,
    canOpen
}) => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const { views, openTo, defaultFormat } = useMemo(() => getModeConfig(selectionType), [selectionType]);
    const effectiveFormat = format ?? defaultFormat;
    const errorMessage = minDateErrorMsg || maxDateErrorMsg;

    const currentValue: Dayjs | null = value ?? null;

    const emit = (next: Dayjs | null) => {
        if (next == null) {
            onChange(null);
            return;
        }
        if (!next.isValid()) return;
        if (selectionType === 'year') onChange(next.startOf('year'));
        else if (selectionType === 'month') onChange(next.startOf('month'));
        else onChange(next);
    };

    const handleAccept = (next: Dayjs | null) => {
        emit(next);
        setOpen(false);
    };

    const shouldDisableDate = (candidate: Dayjs) => !!(enabledFrom && candidate.isBefore(enabledFrom, 'day'));
    const shouldDisableMonth = (candidate: Dayjs) =>
        !!(enabledFrom && candidate.endOf('month').isBefore(enabledFrom, 'day'));
    const shouldDisableYear = (candidate: Dayjs) =>
        !!(enabledFrom && candidate.endOf('year').isBefore(enabledFrom, 'day'));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const HeaderSlot = (headerProps: any) => (
        <DatePickerFTDSHeader
            {...headerProps}
            selectionType={selectionType}
            selectedValue={currentValue}
            enabledFrom={enabledFrom}
            onYearStep={emit}
        />
    );

    const picker = (
        <StyledDatePicker
            value={currentValue}
            onChange={(next) => emit(next)}
            onAccept={handleAccept}
            open={open}
            onOpen={() => {
                if (disabled || (canOpen && !canOpen())) return;
                setOpen(true);
            }}
            onClose={() => setOpen(false)}
            disabled={disabled}
            views={views}
            openTo={openTo}
            format={effectiveFormat}
            onError={(newError) => {
                onValidationError?.(newError);
            }}
            minDate={startDate}
            maxDate={endDate}
            referenceDate={referenceDate}
            reduceAnimations
            shouldDisableDate={shouldDisableDate}
            shouldDisableMonth={shouldDisableMonth}
            shouldDisableYear={shouldDisableYear}
            slots={{
                calendarHeader: HeaderSlot,
                openPickerIcon: () => (
                    <DateIconAdornment hasError={error} aria-label="Open date picker">
                        {error ? (
                            <InfoTooltip message={errorMessage} placement="bottom" bordered open>
                                <ErrorIcon
                                    height={20}
                                    width={20}
                                    className="error-svg"
                                    sx={{ color: theme.palette.error.main, cursor: 'pointer' }}
                                />
                            </InfoTooltip>
                        ) : (
                            <CalendarMonthIcon />
                        )}
                    </DateIconAdornment>
                )
            }}
            slotProps={{
                textField: {
                    placeholder: placeholder ?? effectiveFormat,
                    error,
                    helperText,
                    InputLabelProps: { shrink: false, sx: { display: 'none' } }
                },
                popper: {
                    placement: 'bottom-end',
                    sx: popperSx(theme),
                    modifiers: [
                        { name: 'offset', options: { offset: [0, 8] } },
                        { name: 'preventOverflow', options: { boundary: 'viewport' } }
                    ]
                }
            }}
        />
    );

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language}
            dateFormats={{ monthShort: 'MMMM' }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {label && (
                    <Typography
                        label={label}
                        variant="labels.small"
                        sx={{ color: theme.palette.semantic.text.default }}
                    />
                )}
                {picker}
            </Box>
        </LocalizationProvider>
    );
};
